// apps/admin/src/components/composer/zone-playlist-editor.tsx
//
// Feature #8 — Multi-item playlist editor.
//
// Renders an ordered list of items for a single zone. Supports:
//   - Drag-to-reorder via @dnd-kit/sortable (same pattern as layers-panel)
//   - Per-item remove button
//   - "Add item" button (opens ZoneBindingPicker in add mode)
//   - Save button that calls PUT /:id/program/items/:zone_id/playlist
//
// Props:
//   composerId  — the composer being edited
//   zoneId      — zone whose items are managed
//   programs    — full programs list from parent (or from the server after last save)
//   onPlaylistSaved — called after a successful save so the parent can refetch

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ProgramItemSummary, ProgramSummary, Transition } from '@displaycms/contracts';
import {
  TransitionControl,
  effectiveTransition,
} from '@/components/composer/transition-control';
import { replaceZonePlaylist } from '@/api/composers';
import { useRegisterSave } from '@/pages/composers/composer-save-registry';
import { toast } from '@/lib/toast';
import { Icon } from '@/components/icon';
import { MediaPreview } from '@/components/media/MediaPreview';
import {
  ZoneBindingPicker,
  type ZoneBindingValue,
} from '@/components/composer/zone-binding-picker';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ZonePlaylistEditorProps {
  composerId: string;
  zoneId: string;
  /** Full programs list from parent. Editor derives zone items from this. */
  programs: ProgramSummary[];
  /** Called after a successful PUT so the parent re-queries. */
  onPlaylistSaved: () => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract items for a specific zone, sorted by order_idx ascending. */
function extractZoneItems(
  programs: ProgramSummary[],
  zoneId: string,
): ProgramItemSummary[] {
  const all: ProgramItemSummary[] = [];
  for (const p of programs) {
    for (const it of p.items) {
      if (it.zone_id === zoneId) all.push(it);
    }
  }
  return all.slice().sort((a, b) => a.order_idx - b.order_idx);
}

/** Build a ProgramItemBinding from a ProgramItemSummary (for the API call). */
function summaryToBinding(
  item: ProgramItemSummary,
  zoneId: string,
): import('@displaycms/contracts').ProgramItemBinding {
  const base = {
    zone_id: zoneId,
    duration_ms: item.duration_ms,
    ...(item.transition ? { transition: item.transition } : {}),
  };
  switch (item.kind) {
    case 'video':
      return {
        ...base,
        kind: 'video',
        resource_version_id: item.resource_version_id,
        ...(item.poster_resource_version_id != null
          ? { poster_resource_version_id: item.poster_resource_version_id }
          : {}),
      };
    case 'web_page':
      return { ...base, kind: 'web_page', source_url: item.source_url };
    case 'rss':
      return { ...base, kind: 'rss', source_url: item.source_url };
    case 'composer':
      return { ...base, kind: 'composer', composer_id: item.composer_id };
    case 'empty':
      return { ...base, kind: 'empty' };
    default:
      return {
        ...base,
        kind: 'image',
        resource_version_id: item.resource_version_id,
      };
  }
}

/** Build a ProgramItemBinding from a ZoneBindingValue (for the add flow). */
function bindingValueToApiBinding(
  value: ZoneBindingValue,
  zoneId: string,
): import('@displaycms/contracts').ProgramItemBinding {
  const base = { zone_id: zoneId, duration_ms: 10_000 };
  switch (value.kind) {
    case 'image':
      return { ...base, kind: 'image', resource_version_id: value.resource_version_id };
    case 'video':
      return { ...base, kind: 'video', resource_version_id: value.resource_version_id };
    case 'web_page':
      return { ...base, kind: 'web_page', source_url: value.url };
    case 'rss':
      return { ...base, kind: 'rss', source_url: value.url };
    case 'composer':
      return { ...base, kind: 'composer', composer_id: value.composer_id };
    case 'empty':
      return { ...base, kind: 'empty' };
  }
}

/** Build a local display label for an item (kind + resource id / url suffix). */
function itemLabel(item: ProgramItemSummary): string {
  switch (item.kind) {
    case 'image':
    case 'video':
      return `${item.kind} — ${item.resource_version_id.slice(0, 8)}…`;
    case 'web_page':
    case 'rss':
      return `${item.kind} — ${item.source_url}`;
    case 'composer':
      return `composer — ${item.composer_id.slice(0, 8)}…`;
    case 'empty':
      return 'empty';
  }
}

// ---------------------------------------------------------------------------
// SortableItemRow
// ---------------------------------------------------------------------------

interface SortableItemRowProps {
  item: ProgramItemSummary;
  onRemove: (id: string) => void;
  onTransitionChange: (id: string, next: Transition | undefined) => void;
  inheritedFrom: Transition | null;
}

function SortableItemRow({
  item,
  onRemove,
  onTransitionChange,
  inheritedFrom,
}: SortableItemRowProps): JSX.Element {
  const { t } = useTranslation('composers');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`playlist-item-${item.id}`}
      className="flex items-center gap-1 rounded-md border px-1 py-1.5 text-xs border-border bg-background hover:bg-accent/40"
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={t('playlist_drag_handle_aria', { defaultValue: 'Drag to reorder' })}
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <Icon name="grip-vertical" size={14} />
      </button>

      {/* Thumbnail (resource-backed items only) */}
      {(item.kind === 'image' || item.kind === 'video') && (
        <MediaPreview
          source="resource"
          resourceVersionId={item.resource_version_id}
          kind={item.kind}
          size="thumb"
        />
      )}

      {/* Duration badge */}
      <span className="text-muted-foreground tabular-nums">
        {item.duration_ms}ms
      </span>

      {/* Label */}
      <span className="flex-1 truncate text-left font-medium">{itemLabel(item)}</span>

      {/* Per-item transition — the shared TransitionControl (now WITH a
          duration, shown for every item incl. 1-item zones) + the effective
          transition so "which wins" is visible. */}
      <div
        className="flex flex-col items-end gap-0.5"
        data-testid={`playlist-transition-${item.id}`}
      >
        <TransitionControl
          value={item.transition}
          mode="item"
          inheritedFrom={inheritedFrom}
          onChange={(next) => onTransitionChange(item.id, next)}
        />
        <span
          className="text-[10px] text-muted-foreground"
          data-testid={`playlist-effective-${item.id}`}
        >
          {(() => {
            const eff = effectiveTransition(item.transition, inheritedFrom);
            if (!eff || eff.effect === 'cut') {
              return t('transition_none', { defaultValue: 'Aucune (coupe)' });
            }
            const label = t(`transition_effect_${eff.effect}`, { defaultValue: eff.effect });
            return item.transition
              ? label
              : `${label} (${t('transition_effective_inherited', { defaultValue: 'hérité' })})`;
          })()}
        </span>
      </div>

      {/* Remove button */}
      <button
        type="button"
        data-testid={`playlist-remove-${item.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.id);
        }}
        aria-label={t('playlist_remove_item', { defaultValue: 'Remove item' })}
        className="text-destructive hover:text-destructive/80"
      >
        <Icon name="x" size={14} />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// ZonePlaylistEditor (public)
// ---------------------------------------------------------------------------

export function ZonePlaylistEditor({
  composerId,
  zoneId,
  programs,
  onPlaylistSaved,
}: ZonePlaylistEditorProps): JSX.Element {
  const { t } = useTranslation('composers');

  // Local ordered items derived from props; user can reorder/remove before saving.
  const [items, setItems] = useState<ProgramItemSummary[]>(() =>
    extractZoneItems(programs, zoneId),
  );
  // The loop default shown as the "Hériter (…)" value on each item (single-
  // program loop = programs[0]). null ⇒ inherit resolves to a hard cut.
  const loopDefault: Transition | null = programs[0]?.default_transition ?? null;
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  // SP3c follow-up: last-saved baseline for dirty-tracking — the serialized
  // BINDINGS (exactly what the PUT sends: order + per-item fields; local/temp
  // ids excluded), seeded from the incoming programs and re-committed after
  // each successful save. Pure comparison — the payload mapping
  // (summaryToBinding) is the SAME one the save has always used (DS §5).
  const [savedBaseline, setSavedBaseline] = useState<string>(() =>
    JSON.stringify(
      extractZoneItems(programs, zoneId).map((it) => summaryToBinding(it, zoneId)),
    ),
  );
  const dirty =
    JSON.stringify(items.map((it) => summaryToBinding(it, zoneId))) !== savedBaseline;

  // ---- DnD ----

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((it) => it.id === active.id);
    const newIdx = items.findIndex((it) => it.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    setItems(arrayMove(items, oldIdx, newIdx));
  }

  // ---- Remove ----

  function handleRemove(id: string): void {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }

  // ---- Per-item transition override ----

  function handleTransitionChange(id: string, next: Transition | undefined): void {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        if (next === undefined) {
          const { transition: _drop, ...rest } = it;
          return rest as ProgramItemSummary;
        }
        return { ...it, transition: next };
      }),
    );
  }

  // ---- Add ----

  function handleBindingChosen(value: ZoneBindingValue): void {
    // Create a temporary local item from the picker value.
    const tempId = `new-${Date.now()}`;
    const newItem: ProgramItemSummary = (() => {
      const base = {
        id: tempId,
        zone_id: zoneId,
        order_idx: items.length + 1,
        duration_ms: 10_000,
      };
      switch (value.kind) {
        case 'image':
          return { ...base, kind: 'image' as const, resource_version_id: value.resource_version_id };
        case 'video':
          return { ...base, kind: 'video' as const, resource_version_id: value.resource_version_id };
        case 'web_page':
          return { ...base, kind: 'web_page' as const, source_url: value.url };
        case 'rss':
          return { ...base, kind: 'rss' as const, source_url: value.url };
        case 'composer':
          return { ...base, kind: 'composer' as const, composer_id: value.composer_id };
        case 'empty':
          return { ...base, kind: 'empty' as const };
      }
    })();
    setItems((prev) => [...prev, newItem]);
    setShowPicker(false);
  }

  // ---- Save ----

  // The persistence path shared by the local button and the unified save
  // orchestrator. Throws on failure (so a registry-driven saveAll can report
  // the failed part); the local button wraps it with its own toasts.
  async function persist(): Promise<void> {
    const snapshot = [...items];
    const bindings = snapshot.map((it) => summaryToBinding(it, zoneId));
    await replaceZonePlaylist(composerId, zoneId, bindings);
    setSavedBaseline(JSON.stringify(bindings));
    await onPlaylistSaved();
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    try {
      await persist();
      toast.success(t('playlist_save_success', { defaultValue: 'Playlist saved' }));
    } catch {
      toast.error(t('playlist_save_failed', { defaultValue: 'Save failed' }));
    } finally {
      setSaving(false);
    }
  }

  // SP3c follow-up: register the zone playlist with the unified "Enregistrer
  // la composition" orchestrator (key `zone_playlists`; no-ops standalone —
  // no provider). The local "Save" button above the registry stays — both
  // triggers run the SAME persist(), payload unchanged (DS §5).
  useRegisterSave({
    key: 'zone_playlists',
    order: 3,
    label: t('playlist_title', { defaultValue: 'Playlist' }),
    dirty,
    save: persist,
  });

  // ---- Render ----

  const itemIds = items.map((it) => it.id);

  return (
    <div className="space-y-2" data-testid="zone-playlist-editor">
      <h4 className="text-xs font-semibold text-muted-foreground">
        {t('playlist_title', { defaultValue: 'Playlist' })}
      </h4>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <ul className="space-y-1">
            {items.map((item) => (
              <SortableItemRow
                key={item.id}
                item={item}
                onRemove={handleRemove}
                onTransitionChange={handleTransitionChange}
                inheritedFrom={loopDefault}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {/* Add item */}
      {showPicker ? (
        <div className="mt-2 rounded border p-2">
          <ZoneBindingPicker
            zoneId={zoneId}
            currentBinding={null}
            composerId={composerId}
            onChange={handleBindingChosen}
          />
          <button
            type="button"
            className="mt-1 text-xs text-muted-foreground underline"
            onClick={() => setShowPicker(false)}
          >
            {t('composers:binding_clear', { defaultValue: 'Cancel' })}
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="playlist-add-item"
          onClick={() => setShowPicker(true)}
          className="flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
        >
          <Icon name="plus" size={12} />
          {t('playlist_add_item', { defaultValue: 'Add item' })}
        </button>
      )}

      {/* Save */}
      <button
        type="button"
        data-testid="playlist-save"
        onClick={() => { void handleSave(); }}
        disabled={saving}
        className="w-full rounded bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-50"
      >
        {saving
          ? t('composers:detail.saving', { defaultValue: 'Saving…' })
          : t('composers:binding_save', { defaultValue: 'Save' })}
      </button>
    </div>
  );
}
